import React, { useState, useCallback, useMemo } from 'react';
import { BuyNewHomeInputs, FilingStatus, InvestmentInputs, HomeInputs, TaxInputs, MortgageRate } from '../types';
import { useBuyNewHomeCalculator, calculateItemizationInfo } from '../hooks/useMortgageCalculator';
import { InputCard, InputRow } from './InputCard';
import { SALT_CAPS } from '../constants';
import { ResultsDisplay } from './ResultsDisplay';
import { DataTable } from './DataTable';
import { TaxInfo } from './TaxInfo';
import { RateSelector } from './RateSelector';
import { InfoTooltip } from './InfoTooltip';
import { generateBuyNewHomePdf } from '../utils/pdfGenerator';

export const BuyNewHomeCalculator: React.FC = () => {
  const [homeInputs, setHomeInputs] = useState<HomeInputs>({
    purchasePrice: 1000000,
    downPayment: 25,
    interestRate: 6.25,
    loanTerm: 30,
    startDate: new Date().toISOString().split('T')[0],
    closingCosts: 20000,
    insuranceSavings: 500,
    appreciation: 3,
    isARM: false,
    armFixedTerm: 5,
    armSubsequentRate: 7.25,
    lumpSums: [],
  });

  const [taxInputs, setTaxInputs] = useState<TaxInputs>({
    filingStatus: FilingStatus.MARRIED_FILING_JOINTLY,
    marginalTaxRate: 24,
    otherItemizedDeductions: 0,
  });
  
  const [saltWarning, setSaltWarning] = useState<boolean>(false);
  const [warnings, setWarnings] = useState<Record<string, string>>({});

  const [investmentInputs, setInvestmentInputs] = useState<InvestmentInputs>({
    initialReturnRate: 4,
    initialReturnPeriod: 10,
    longTermReturnRate: 6,
    taxRateOnGains: 20,
  });

  const [submittedInputs, setSubmittedInputs] = useState<BuyNewHomeInputs | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  const results = useBuyNewHomeCalculator(submittedInputs);
  const itemizationInfo = useMemo(() => calculateItemizationInfo(homeInputs, taxInputs), [homeInputs, taxInputs]);
  const saltCapForStatus = useMemo(() => SALT_CAPS[taxInputs.filingStatus], [taxInputs.filingStatus]);
  
  const downPaymentAmount = useMemo(() => homeInputs.purchasePrice * (homeInputs.downPayment / 100), [homeInputs.purchasePrice, homeInputs.downPayment]);
  const loanAmount = useMemo(() => homeInputs.purchasePrice - downPaymentAmount, [homeInputs.purchasePrice, downPaymentAmount]);

  const handleHomeChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    setHomeInputs(prev => {
        let newInputs = { ...prev };
        
        if (type === 'checkbox') {
            newInputs.isARM = (e.target as HTMLInputElement).checked;
        } else if (name === 'purchasePrice' || name === 'loanAmount' || name === 'closingCosts' || name === 'insuranceSavings') {
            const numValue = Number(value.replace(/[^0-9]/g, '')) || 0;
            if (name === 'purchasePrice') {
                newInputs.purchasePrice = numValue;
            } else if (name === 'loanAmount') {
                 const newLoanAmount = Math.max(0, Math.min(newInputs.purchasePrice, numValue));
                 if (newInputs.purchasePrice > 0) {
                    newInputs.downPayment = parseFloat(((1 - newLoanAmount / newInputs.purchasePrice) * 100).toFixed(4));
                 } else {
                    newInputs.downPayment = 0;
                 }
            } else { // closingCosts or insuranceSavings
                 newInputs[name] = numValue;
            }
        } else if (type === 'number') {
            const numValue = parseFloat(value) || 0;
            const newWarnings = { ...warnings };
            
            if (name === 'downPayment') {
                newInputs.downPayment = Math.max(0, Math.min(100, numValue));
                if (numValue < 0 || numValue > 100) newWarnings.downPayment = "Value clamped between 0 and 100%";
                else delete newWarnings.downPayment;
            }
            else if (name === 'interestRate') {
                newInputs.interestRate = Math.max(0, Math.min(30, numValue));
                if (numValue < 0 || numValue > 30) newWarnings.interestRate = "Value clamped between 0 and 30%";
                else delete newWarnings.interestRate;
            }
            else if (name === 'armSubsequentRate') {
                newInputs.armSubsequentRate = Math.max(0, Math.min(30, numValue));
                if (numValue < 0 || numValue > 30) newWarnings.armSubsequentRate = "Value clamped between 0 and 30%";
                else delete newWarnings.armSubsequentRate;
            }
            else if (name === 'loanTerm') {
                newInputs.loanTerm = Math.max(1, Math.min(50, numValue));
                if (numValue < 1 || numValue > 50) newWarnings.loanTerm = "Value clamped between 1 and 50 years";
                else delete newWarnings.loanTerm;
            }
            else if (name === 'armFixedTerm') {
                newInputs.armFixedTerm = Math.max(1, Math.min(50, numValue));
                if (numValue < 1 || numValue > 50) newWarnings.armFixedTerm = "Value clamped between 1 and 50 years";
                else delete newWarnings.armFixedTerm;
            }
            else if (name === 'appreciation') {
                newInputs.appreciation = Math.max(-10, Math.min(20, numValue));
                if (numValue < -10 || numValue > 20) newWarnings.appreciation = "Value clamped between -10% and 20%";
                else delete newWarnings.appreciation;
            }
            else {
                (newInputs as any)[name] = Math.max(0, numValue);
            }
            setWarnings(newWarnings);
        } else {
            (newInputs as any)[name] = value;
        }

        return newInputs;
    });

    if (submittedInputs) setSubmittedInputs(null);
  };

  const handleTaxChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    setTaxInputs(prev => {
        let finalValue: string | number;
        if (name === 'otherItemizedDeductions') {
            finalValue = Number(value.replace(/[^0-9]/g, '')) || 0;
        } else if (name === 'marginalTaxRate') {
            finalValue = Math.max(0, Math.min(100, parseFloat(value) || 0));
        } else if ((e.target as HTMLInputElement).type === 'number') {
            finalValue = parseFloat(value) || 0;
        } else {
            finalValue = value;
        }
        const newInputs = { ...prev, [name]: finalValue };
        setSaltWarning(newInputs.otherItemizedDeductions > SALT_CAPS[newInputs.filingStatus]);
        return newInputs;
    });
    if (submittedInputs) setSubmittedInputs(null);
  };

  const handleInvestmentChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    let finalValue: string | number;
    if ((e.target as HTMLInputElement).type === 'number') {
        const numValue = parseFloat(value) || 0;
        if (['initialReturnRate', 'longTermReturnRate'].includes(name)) finalValue = Math.max(-20, Math.min(50, numValue));
        else if (name === 'taxRateOnGains') finalValue = Math.max(0, Math.min(100, numValue));
        else finalValue = Math.max(0, numValue);
    } else {
        finalValue = value;
    }
    setInvestmentInputs(prev => ({ ...prev, [name]: finalValue }));
    if (submittedInputs) setSubmittedInputs(null);
  };


  const handleCalculate = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmittedInputs(null);
    setTimeout(() => {
      setSubmittedInputs({
        home: homeInputs,
        tax: taxInputs,
        investment: investmentInputs,
      });
      setTimeout(() => setIsLoading(false), 300);
    }, 50);
  }, [homeInputs, taxInputs, investmentInputs]);

  const handleReset = () => {
    setSubmittedInputs(null);
  }
  
  const handleDownloadPdf = () => {
    if (submittedInputs && results) {
      generateBuyNewHomePdf(submittedInputs, results);
    }
  };

  const handleRateSelect = (rate: MortgageRate) => {
    setHomeInputs(prev => ({
      ...prev,
      interestRate: rate.rate,
      loanTerm: rate.term,
    }));
     if (submittedInputs) setSubmittedInputs(null);
  };

  const addLumpSum = () => {
    setHomeInputs(prev => ({
      ...prev,
      lumpSums: [...prev.lumpSums, { amount: 10000, yearsFromNow: 2 }]
    }));
    if (submittedInputs) setSubmittedInputs(null);
  };

  const updateLumpSum = (index: number, key: 'amount' | 'yearsFromNow', value: number) => {
    setHomeInputs(prev => {
      const newList = [...prev.lumpSums];
      newList[index] = { ...newList[index], [key]: value };
      return { ...prev, lumpSums: newList };
    });
    if (submittedInputs) setSubmittedInputs(null);
  };

  const removeLumpSum = (index: number) => {
    setHomeInputs(prev => ({
      ...prev,
      lumpSums: prev.lumpSums.filter((_, i) => i !== index)
    }));
    if (submittedInputs) setSubmittedInputs(null);
  };

  const inputClass = "w-full p-2 border rounded bg-white dark:bg-slate-700 dark:border-slate-600 dark:text-white transition-colors focus:ring-2 focus:ring-bp-light-blue outline-none";
  const readOnlyInputClass = "w-full p-2 border rounded bg-gray-100 dark:bg-slate-600 dark:border-slate-500 dark:text-gray-300";
  
  return (
    <>
      <header className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-bp-slate dark:text-white">Finance vs. Pay Cash Calculator</h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 mt-2">Should you get a mortgage or buy your home with cash?</p>
      </header>
    
      <main>
        <RateSelector onRateSelect={handleRateSelect} />
        <form onSubmit={handleCalculate}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              {/* Left Column */}
              <div className="flex flex-col gap-6">
                  <InputCard title="Home Purchase Details">
                      <InputRow label="Purchase Price ($)" id="purchasePrice">
                          <input type="text" inputMode="numeric" id="purchasePrice" name="purchasePrice" value={homeInputs.purchasePrice.toLocaleString('en-US')} onChange={handleHomeChange} className={inputClass} required />
                      </InputRow>
                      <InputRow label="Down Payment (%)" id="downPayment">
                          <input type="number" step="0.1" id="downPayment" name="downPayment" value={homeInputs.downPayment} onChange={handleHomeChange} className={inputClass} min="0" max="100" required />
                          {warnings.downPayment && <p className="text-xs text-amber-600 mt-1">{warnings.downPayment}</p>}
                      </InputRow>
                       <InputRow label="Down Payment Amount ($)" id="downPaymentAmount">
                        <input type="text" value={downPaymentAmount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} className={readOnlyInputClass} readOnly />
                      </InputRow>
                       <InputRow label="Loan Amount ($)" id="loanAmount">
                        <input type="text" inputMode="numeric" id="loanAmount" name="loanAmount" value={loanAmount.toLocaleString('en-US')} onChange={handleHomeChange} className={inputClass} />
                      </InputRow>
                       <InputRow label="Interest Rate (%)" id="interestRate">
                          <input type="number" step="0.001" id="interestRate" name="interestRate" value={homeInputs.interestRate} onChange={handleHomeChange} className={inputClass} min="0" max="30" required />
                          {warnings.interestRate && <p className="text-xs text-amber-600 mt-1">{warnings.interestRate}</p>}
                      </InputRow>

                      {/* ARM Toggle & Inputs */}
                    <div className="py-2">
                        <div className="flex items-center mb-2">
                            <input 
                                type="checkbox" 
                                id="isARM" 
                                name="isARM" 
                                checked={homeInputs.isARM} 
                                onChange={handleHomeChange}
                                className="w-4 h-4 text-bp-dark-blue border-gray-300 rounded focus:ring-bp-light-blue"
                            />
                            <label htmlFor="isARM" className="ml-2 font-semibold text-gray-700 dark:text-gray-300">Is this an ARM?</label>
                        </div>
                        
                        {homeInputs.isARM && (
                            <div className="pl-6 border-l-2 border-blue-200 dark:border-blue-800 space-y-3 bg-blue-50 dark:bg-blue-900/10 p-3 rounded-r-lg">
                                <InputRow label="Fixed Period (Yrs)" id="armFixedTerm">
                                     <div className="flex flex-col w-full">
                                        <input type="number" step="1" id="armFixedTerm" name="armFixedTerm" value={homeInputs.armFixedTerm} onChange={handleHomeChange} className={inputClass} min="1" max="30" />
                                        {warnings.armFixedTerm && <p className="text-xs text-amber-600 mt-1">{warnings.armFixedTerm}</p>}
                                     </div>
                                </InputRow>
                                <InputRow label="Future Rate (%)" id="armSubsequentRate">
                                    <div className="flex flex-col w-full">
                                        <input type="number" step="0.125" id="armSubsequentRate" name="armSubsequentRate" value={homeInputs.armSubsequentRate} onChange={handleHomeChange} className={inputClass} min="0" max="30" />
                                        {warnings.armSubsequentRate && <p className="text-xs text-amber-600 mt-1">{warnings.armSubsequentRate}</p>}
                                    </div>
                                </InputRow>
                            </div>
                        )}
                    </div>

                      <InputRow label="Loan Term (Years)" id="loanTerm">
                          <input type="number" id="loanTerm" name="loanTerm" value={homeInputs.loanTerm} onChange={handleHomeChange} className={inputClass} min="1" max="50" required />
                          {warnings.loanTerm && <p className="text-xs text-amber-600 mt-1">{warnings.loanTerm}</p>}
                      </InputRow>
                       <InputRow label="Annual Appreciation (%)" id="appreciation">
                          <input type="number" step="0.1" id="appreciation" name="appreciation" value={homeInputs.appreciation} onChange={handleHomeChange} className={inputClass} min="-10" max="20" required />
                          {warnings.appreciation && <p className="text-xs text-amber-600 mt-1">{warnings.appreciation}</p>}
                      </InputRow>
                      <InputRow label="Start Date" id="startDate">
                          <input type="date" id="startDate" name="startDate" value={homeInputs.startDate} onChange={handleHomeChange} className={inputClass} required />
                      </InputRow>
                  </InputCard>
                  <InputCard title="Financing-Specific Costs">
                      <InputRow label="Mortgage Closing Costs ($)" id="closingCosts">
                         <div className='flex items-center'>
                            <input type="text" inputMode="numeric" id="closingCosts" name="closingCosts" value={homeInputs.closingCosts.toLocaleString('en-US')} onChange={handleHomeChange} className={inputClass} />
                            <InfoTooltip text="Enter the estimated one-time closing costs for the mortgage (e.g., appraisal, origination fees). This amount is not invested in the 'Finance' scenario." />
                          </div>
                      </InputRow>
                      <InputRow label="Insurance Savings (Cash)" id="insuranceSavings">
                          <div className='flex items-center'>
                            <input type="text" inputMode="numeric" id="insuranceSavings" name="insuranceSavings" value={homeInputs.insuranceSavings.toLocaleString('en-US')} onChange={handleHomeChange} className={inputClass} />
                            <InfoTooltip text="Cash buyers can often get cheaper insurance by choosing a high deductible (e.g., 3-5% of the home's value), which lenders don't allow. Enter the estimated annual savings here. This amount is invested monthly in the 'Pay Cash' scenario." />
                          </div>
                      </InputRow>
                  </InputCard>
              </div>
              {/* Right Column */}
              <div className="flex flex-col gap-6">
                   <InputCard title="Tax Assumptions">
                      <InputRow label="Filing Status" id="filingStatus">
                          <select id="filingStatus" name="filingStatus" value={taxInputs.filingStatus} onChange={handleTaxChange} className={inputClass}>
                          {Object.values(FilingStatus).map(status => (
                              <option key={status} value={status}>{status}</option>
                          ))}
                          </select>
                      </InputRow>
                      <InputRow label="Marginal Tax Rate (%)" id="marginalTaxRate">
                          <input type="number" id="marginalTaxRate" name="marginalTaxRate" value={taxInputs.marginalTaxRate} onChange={handleTaxChange} className={inputClass} min="0" max="100" required />
                      </InputRow>
                      <InputRow label="Other Deductions ($)" id="otherItemizedDeductions">
                        <div className="flex items-center">
                          <input type="text" inputMode="numeric" id="otherItemizedDeductions" name="otherItemizedDeductions" value={taxInputs.otherItemizedDeductions.toLocaleString('en-US')} onChange={handleTaxChange} className={inputClass} required />
                          <InfoTooltip text="Enter other annual itemized deductions. Note: The State and Local Tax (SALT) deduction cap depends on your filing status and increases by 1% annually under current tax law. This helps determine if you'd benefit from the mortgage interest deduction." />
                        </div>
                      </InputRow>
                      {saltWarning && (
                        <div className="text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 p-2 rounded-md -mt-2 border border-amber-200 dark:border-amber-800">
                            Heads up: The State and Local Tax (SALT) deduction is capped at {saltCapForStatus.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })} for your filing status. Your entry may overstate your tax savings.
                        </div>
                      )}
                      <TaxInfo itemizationInfo={itemizationInfo} />
                  </InputCard>
                  <InputCard title="Investment Assumptions">
                      <p className="text-sm text-gray-600 dark:text-gray-400 -mt-2 mb-2">Applied to invested capital in both scenarios.</p>
                      <InputRow label="Projected Initial Return (%)" id="initialReturnRate">
                          <input type="number" step="0.1" id="initialReturnRate" name="initialReturnRate" value={investmentInputs.initialReturnRate} onChange={handleInvestmentChange} className={inputClass} min="-20" max="50" required />
                      </InputRow>
                       <InputRow label="For First (Years)" id="initialReturnPeriod">
                           <input type="number" step="1" id="initialReturnPeriod" name="initialReturnPeriod" value={investmentInputs.initialReturnPeriod} onChange={handleInvestmentChange} className={inputClass} min="0" max="50" required />
                      </InputRow>
                      <InputRow label="Long-Term Return Rate (%)" id="longTermReturnRate">
                          <input type="number" step="0.1" id="longTermReturnRate" name="longTermReturnRate" value={investmentInputs.longTermReturnRate} onChange={handleInvestmentChange} className={inputClass} min="-20" max="50" required />
                      </InputRow>
                      <InputRow label="Tax Rate on Gains (%)" id="taxRateOnGains">
                          <input type="number" id="taxRateOnGains" name="taxRateOnGains" value={investmentInputs.taxRateOnGains} onChange={handleInvestmentChange} className={inputClass} min="0" max="100" required />
                      </InputRow>
                  </InputCard>
                  <InputCard title="Lump Sum Payments (Optional)">
                      <p className="text-sm text-gray-600 dark:text-gray-400 -mt-2 mb-4">
                        Modeled as a mortgage paydown in "Finance" scenario and an investment contribution in "Pay Cash" scenario.
                      </p>
                      <div className="space-y-4">
                        {homeInputs.lumpSums.map((lump, index) => (
                          <div key={index} className="grid grid-cols-3 gap-2 items-center">
                            <div className="col-span-1">
                                <label className="block text-xs uppercase font-semibold text-gray-500 mb-1 font-mono">Amount ($)</label>
                                <input 
                                  type="text"
                                  inputMode="numeric"
                                  value={lump.amount.toLocaleString('en-US')}
                                  onChange={(e) => updateLumpSum(index, 'amount', Number(e.target.value.replace(/[^0-9]/g, '')))}
                                  className={inputClass}
                                />
                            </div>
                            <div className="col-span-1 relative pt-5">
                                <label className="absolute top-0 left-0 block text-xs uppercase font-semibold text-gray-500 font-mono">Year</label>
                                <input 
                                  type="number" 
                                  value={lump.yearsFromNow}
                                  onChange={(e) => updateLumpSum(index, 'yearsFromNow', parseFloat(e.target.value))}
                                  className={`${inputClass} pr-12`}
                                  min="0"
                                />
                                <span className="absolute right-2 top-[34px] text-gray-400 text-xs">yrs</span>
                            </div>
                            <div className="col-span-1 pt-5 text-right">
                                <button type="button" onClick={() => removeLumpSum(index)} className="text-red-500 hover:text-red-700 dark:hover:text-red-400 font-semibold text-sm">
                                    Remove
                                </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <button type="button" onClick={addLumpSum} className="mt-4 w-full bg-gray-200 hover:bg-gray-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-800 dark:text-gray-200 font-bold py-2 px-4 rounded-lg transition-colors">
                          + Add Lump Sum
                      </button>
                   </InputCard>
              </div>
          </div>
          
          <div className="mt-6 text-center">
            <button type="submit" className="bg-bp-dark-blue hover:bg-bp-slate text-white font-bold py-3 px-8 rounded-lg text-lg transition-colors shadow-lg disabled:bg-gray-400" disabled={isLoading}>
              {isLoading ? 'Calculating...' : 'Calculate'}
            </button>
            {results && (
              <>
                <button type="button" onClick={handleReset} className="ml-4 bg-gray-500 hover:bg-gray-600 dark:bg-slate-600 dark:hover:bg-slate-500 text-white font-bold py-3 px-8 rounded-lg text-lg transition-colors shadow-lg">
                  Reset
                </button>
                <button type="button" onClick={handleDownloadPdf} className="ml-4 bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-8 rounded-lg text-lg transition-colors shadow-lg">
                  Download PDF Report
                </button>
              </>
            )}
          </div>
        </form>

        <div className="mt-8">
            <ResultsDisplay results={results} isLoading={isLoading} />
        </div>

        {results && !isLoading && (
          <>
            <DataTable data={results.monthlyData} />
          </>
        )}
      </main>
    </>
  );
};